import { useEffect, useRef, useState } from "react";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";

interface VirtualKeyboardProps {
  input: string;
  onInputChange: (input: string) => void;
  language: "en" | "gu";
}

// English QWERTY + punctuation (with default and shift layouts)
const englishLayout: Record<string, string[]> = {
  default: [
    "` 1 2 3 4 5 6 7 8 9 0 - = {backspace}",
    "q w e r t y u i o p",
    "a s d f g h j k l",
    "{shift} z x c v b n m",
    ". , ! ? ; : - _ / @ # $ % & * ( )",
    "{space}",
  ],
  shift: [
    "` 1 2 3 4 5 6 7 8 9 0 - = {backspace}",
    "Q W E R T Y U I O P",
    "A S D F G H J K L",
    "{shift} Z X C V B N M",
    ". , ! ? ; : - _ / @ # $ % & * ( )",
    "{space}",
  ],
};

// Gujarati layout (unchanged, no shift required)
const gujaratiLayout: Record<string, string[]> = {
  default: [
    "1 2 3 4 5 6 7 8 9 0 {backspace}",
    "ા િ ી ુ ૂ ૃ ૄ ે ૈ ો ૌ ્", // Added matras for basic composition
    "ઁ ં ઃ અ આ ઇ ઈ ઉ ઊ ઋ ઍ એ ઐ",
    "ઑ ઓ ઔ ક ખ ગ ઘ ઙ ચ છ જ ઝ ઞ",
    "ટ ઠ ડ ઢ ણ ત થ દ ધ ન પ ફ બ ભ મ",
    "ય ર લ વ શ ષ સ હ ળ ? ક્ષ જ્ઞ",
    "{space}",
  ],
};

export default function VirtualKeyboard({
  input,
  onInputChange,
  language,
}: VirtualKeyboardProps) {
  const keyboardRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [layoutName, setLayoutName] = useState<"default" | "shift">("default");

  // Sync keyboard with input changes
  useEffect(() => {
    if (keyboardRef.current) {
      keyboardRef.current.setInput(input);
    }
    if (inputRef.current) {
      inputRef.current.value = input;
    }
  }, [input]);

  // Handle keyboard input from react-simple-keyboard
  const handleKeyboardInput = (inputValue: string) => {
    onInputChange(inputValue);
  };

  // Handle key press events
  const handleKeyPress = (button: string) => {
    if (button === "{shift}") {
      // Toggle between default and shift layouts for English only
      if (language === "en") {
        setLayoutName((prev) => (prev === "default" ? "shift" : "default"));
      }
      return;
    }
    if (button === "{backspace}") {
      const newInput = input.slice(0, -1);
      onInputChange(newInput);
      if (inputRef.current) {
        inputRef.current.value = newInput;
      }
    } else if (button === "{space}") {
      const newInput = input + " ";
      onInputChange(newInput);
      if (inputRef.current) {
        inputRef.current.value = newInput;
      }
    } else {
      const newInput = input + button;
      onInputChange(newInput);
      if (inputRef.current) {
        inputRef.current.value = newInput;
      }
    }
    // Reset to default layout after a key press (for one-time shift)
    if (layoutName === "shift" && button !== "{shift}") {
      setLayoutName("default");
    }
  };

  // Handle direct input from the native input field
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onInputChange(newValue);
    if (keyboardRef.current) {
      keyboardRef.current.setInput(newValue);
    }
  };

  return (
    <>
      <style>{`
        /* Dark mode text visibility */
        .dark .hg-theme-default .hg-button {
          color: #ffffff !important;
          background-color: hsl(220, 15%, 18%) !important;
          border-color: hsl(220, 15%, 25%) !important;
        }
        .dark .hg-theme-default .hg-button.hg-activeButton {
          background-color: hsl(197, 90%, 40%) !important;
          color: #ffffff !important;
        }
        .dark .hg-theme-default .hg-button.hg-functionBtn {
          background-color: hsl(220, 15%, 22%) !important;
          color: #ffffff !important;
        }
        
        /* Light mode styling */
        .hg-theme-default .hg-button {
          color: hsl(210, 25%, 15%) !important;
          background-color: hsl(0, 0%, 100%) !important;
        }
        
        /* Mobile responsiveness */
        @media (max-width: 640px) {
          .hg-theme-default .hg-button {
            font-size: 10px !important;
            padding: 4px 2px !important;
            min-width: 24px !important;
            height: 32px !important;
            line-height: 1.2 !important;
          }
          .hg-theme-default .hg-row {
            margin-bottom: 2px !important;
          }
          .hg-theme-default {
            padding: 4px !important;
          }
          .hg-theme-default .hg-button.hg-functionBtn {
            min-width: 40px !important;
            font-size: 9px !important;
          }
          
          /* Dark mode mobile */
          .dark .hg-theme-default .hg-button {
            color: #ffffff !important;
            font-size: 10px !important;
          }
        }
      `}</style>
      <div className="w-full overflow-x-auto px-6 sm:px-6" style={{ scrollbarWidth: 'thin' }}>
        {/* Native input field for IME support */}
        {/* <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={handleInputChange}
          style={{ width: "100%", padding: "10px", marginBottom: "10px" }}
          placeholder="Type here..."
          lang={language === "gu" ? "gu" : "en"}
          autoCapitalize="none"
          autoCorrect="off"
        /> */}
        {/* Virtual keyboard */}
        <div className="min-w-max pb-1">
          <Keyboard
            keyboardRef={(r) => (keyboardRef.current = r)}
            onChange={handleKeyboardInput}
            onKeyPress={handleKeyPress}
            layout={language === "gu" ? gujaratiLayout : englishLayout}
            layoutName={language === "en" ? layoutName : "default"}
            physicalKeyboardHighlight
            syncInstanceInputs
            theme={`hg-theme-default ${language === "gu" ? "gu" : "en"}`}
            className="w-full"
          />
        </div>
      </div>
    </>
  );
}